import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendar, FaEuroSign, FaBox } from 'react-icons/fa';
import { bookingService } from '../services/api';
import '../styles/BookingDetail.css';

function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBooking();
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const response = await bookingService.getById(id);
      setBooking(response.booking);
    } catch (error) {
      console.error('Error loading booking:', error);
      alert('Fehler beim Laden der Buchung');
      navigate('/feierpakete');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Buchung wirklich löschen?')) return;
    
    try {
      await bookingService.delete(id);
      navigate('/feierpakete');
    } catch (error) {
      alert('Fehler beim Löschen: ' + error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await bookingService.update(id, { status: newStatus });
      setBooking({ ...booking, Status: newStatus });
    } catch (error) {
      alert('Fehler beim Aktualisieren: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'Angefragt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Versendet': 'versendet',
      'Zurück': 'zurueck',
      'Abgeschlossen': 'abgeschlossen',
      'Bezahlt': 'bestaetigt'
    };
    return statusMap[status] || 'neu';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!booking) {
    return <div className="card">Buchung nicht gefunden</div>;
  }

  return (
    <div className="booking-detail-page">
      <div className="page-header">
        <div>
          <Link to="/feierpakete" className="back-button">
            <FaArrowLeft /> Zurück zur Übersicht
          </Link>
          <h1>📦 {booking.Vorname} {booking.Nachname}</h1>
          <span className={`status-badge status-${getStatusColor(booking.Status)}`}>
            {booking.Status || 'Neu'}
          </span>
        </div>
        <div className="header-actions">
          <Link to={`/feierpakete/${id}/edit`} className="btn btn-secondary">
            <FaEdit /> Bearbeiten
          </Link>
          <button onClick={handleDelete} className="btn btn-outline" style={{ borderColor: '#dc2626', color: '#dc2626' }}>
            <FaTrash /> Löschen
          </button>
        </div>
      </div>

      {/* Status Workflow */}
      <div className="card mb-3">
        <h2 className="mb-3">Status ändern</h2>
        <div className="status-buttons">
          {['Neu', 'Angefragt', 'Bestätigt', 'Bezahlt', 'Versendet', 'Zurück', 'Abgeschlossen'].map(status => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`status-button ${booking.Status === status ? 'active' : ''} status-${getStatusColor(status)}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Info Grid */}
      <div className="info-grid">
        {/* Kundendaten */}
        <div className="card info-card">
          <h2>Kundendaten</h2>
          <div className="info-row">
            <FaEnvelope />
            <div>
              <label>E-Mail</label>
              <a href={`mailto:${booking['E-Mail']}`}>{booking['E-Mail']}</a>
            </div>
          </div>
          <div className="info-row">
            <FaPhone />
            <div>
              <label>Telefon</label>
              <a href={`tel:${booking.Telefon}`}>{booking.Telefon}</a>
            </div>
          </div>
          <div className="info-row">
            <FaMapMarkerAlt />
            <div>
              <label>Adresse</label>
              <p>
                {booking.Adresse}<br />
                {booking.PLZ} {booking.Ort}<br />
                {booking.Land || 'Deutschland'}
              </p>
            </div>
          </div>
        </div>

        {/* Buchungsdetails */}
        <div className="card info-card">
          <h2>Buchungsdetails</h2>
          <div className="info-row">
            <FaBox />
            <div>
              <label>Paket-ID</label>
              <p>{booking.Paket_ID || '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaEuroSign />
            <div>
              <label>Preis</label>
              <p className="text-primary"><strong>{booking.Preis ? `${booking.Preis}€` : '-'}</strong></p>
            </div>
          </div>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Zahlungsstatus</label>
              <p>
                <span className={`status-badge status-${booking.Zahlungsstatus === 'Bezahlt' ? 'bestaetigt' : 'neu'}`}>
                  {booking.Zahlungsstatus || 'Offen'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Datum-Übersicht */}
      <div className="card">
        <h2>Datumsübersicht</h2>
        <div className="info-grid">
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Eventdatum</label>
              <p>{booking.Eventdatum ? new Date(booking.Eventdatum).toLocaleDateString('de-DE') : '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Versanddatum</label>
              <p>{booking.Versanddatum ? new Date(booking.Versanddatum).toLocaleDateString('de-DE') : '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Rücksendung ab</label>
              <p>{booking.Ruecksendung_ab ? new Date(booking.Ruecksendung_ab).toLocaleDateString('de-DE') : '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Reservierung bis</label>
              <p>{booking.Reservierung_ablauf ? new Date(booking.Reservierung_ablauf).toLocaleDateString('de-DE') : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notizen */}
      {booking.Notizen && (
        <div className="card">
          <h2>Notizen</h2>
          <p className="notes-text">{booking.Notizen}</p>
        </div>
      )}
    </div>
  );
}

export default BookingDetail;
